import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  createBidResponseTags,
  deleteBidResponseTag,
} from '../../../../../shared/api/magellan/bidResponseTags';
import type { CreateBidResponseTagRequestDTO } from '../../../../../shared/api/magellan/bidResponseTags/dto';
import { getBidResponseById } from '../../../../../shared/api/magellan/bidResponses';
import type { GetBidResponsesResponseDTO } from '../../../../../shared/api/magellan/bidResponses/dto';
import { displayErrorNotification } from '../../../../../shared/components/notifications/errorNotification';
import type { BidResponse } from '../../../../../shared/entities/BidResponse';
import type { VaultProperty } from '../../../../../shared/entities/VaultProperty';
import { getBidResponsesQueryKey } from './bidResponse.hook';
import { getVaultPropertiesQueryKey } from './vaultProperty.hook';

export function useCreateBidResponseTagMutation(bidResponseId: number) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['notification', 'bidResponse']);
  const getBidResponseByIdQueryKey = [getBidResponseById.name, bidResponseId];

  return useMutation({
    mutationFn: ({
      bidResponseId,
      propertyId,
      payload,
    }: {
      bidResponseId: number;
      propertyId: number;
      payload: CreateBidResponseTagRequestDTO;
    }) => createBidResponseTags({ bidResponseId, propertyId }, payload),
    onMutate: async ({ bidResponseId, propertyId, payload }) => {
      await queryClient.cancelQueries({ queryKey: getBidResponsesQueryKey });
      await queryClient.cancelQueries({ queryKey: getBidResponseByIdQueryKey });

      const previousGetBidResponses = optimisticUpdateBidResponses(
        queryClient,
        bidResponseId,
        propertyId,
        payload,
      );

      const previousGetBidResponseById = optimisticUpdateBidResponse(
        queryClient,
        bidResponseId,
        propertyId,
        payload,
      );

      return {
        previousGetBidResponses,
        previousGetBidResponseById,
      };
    },

    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        getBidResponsesQueryKey,
        context?.previousGetBidResponses,
      );

      queryClient.setQueryData(
        getBidResponseByIdQueryKey,
        context?.previousGetBidResponseById,
      );

      displayErrorNotification(
        t('notification:errors.somethingWentWrong.title'),
        t('notification:errors.somethingWentWrong.message'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponsesQueryKey });
      queryClient.invalidateQueries({ queryKey: getBidResponseByIdQueryKey });
    },
  });
}

function optimisticUpdateBidResponses(
  queryClient: QueryClient,
  bidResponseId: number,
  propertyId: number,
  payload: CreateBidResponseTagRequestDTO,
) {
  const queryKey = getBidResponsesQueryKey;
  const properties = queryClient.getQueryData<VaultProperty[]>(
    getVaultPropertiesQueryKey,
  );
  const previousGetBidResponses =
    queryClient.getQueryData<GetBidResponsesResponseDTO>(queryKey);
  if (!previousGetBidResponses) {
    return;
  }

  const currentBidResponse = previousGetBidResponses.bidResponses.find(
    bidResponse => bidResponse.id === bidResponseId,
  );

  if (!currentBidResponse) {
    return;
  }

  const newTag = properties
    ?.find(property => property.id === propertyId)
    ?.options.find(option => option.id === payload.vaultPropertyOptionId);

  if (!newTag) {
    return;
  }

  const currentProperty = currentBidResponse.properties.find(
    property => property.propertyId === propertyId,
  );

  if (!currentProperty) {
    return;
  }

  const updatedCurrentBidResponse = {
    ...currentBidResponse,
    properties: [
      ...currentBidResponse.properties.filter(
        property => property.propertyId !== propertyId,
      ),
      {
        propertyId,
        values: [
          ...currentProperty.values,
          {
            tagId: -1,
            name: newTag.name,
          },
        ],
      },
    ],
  };

  const updatedBidResponses = [
    ...previousGetBidResponses.bidResponses.filter(
      bidResponse => bidResponse.id !== bidResponseId,
    ),
    updatedCurrentBidResponse,
  ];

  const sortedUpdatedBidResponses = updatedBidResponses.sort(
    (a, b) =>
      b.responseDeadline.localeCompare(a.responseDeadline) ||
      a.title.localeCompare(b.title),
  );

  queryClient.setQueryData(queryKey, {
    bidResponses: sortedUpdatedBidResponses,
  });

  return previousGetBidResponses;
}

function optimisticUpdateBidResponse(
  queryClient: QueryClient,
  bidResponseId: number,
  propertyId: number,
  payload: CreateBidResponseTagRequestDTO,
) {
  const getBidResponseByIdQueryKey = [getBidResponseById.name, bidResponseId];
  const properties = queryClient.getQueryData<VaultProperty[]>(
    getVaultPropertiesQueryKey,
  );
  const previousGetBidResponseById = queryClient.getQueryData<BidResponse>(
    getBidResponseByIdQueryKey,
  );

  if (!previousGetBidResponseById) {
    return;
  }

  const newTag = properties
    ?.find(property => property.id === propertyId)
    ?.options.find(option => option.id === payload.vaultPropertyOptionId);

  if (!newTag) {
    return;
  }

  const currentProperty = previousGetBidResponseById.properties.find(
    property => property.propertyId === propertyId,
  );

  if (!currentProperty) {
    return;
  }

  const updatedCurrentBidResponse = {
    ...previousGetBidResponseById,
    properties: [
      ...previousGetBidResponseById.properties.filter(
        property => property.propertyId !== propertyId,
      ),
      {
        ...currentProperty,
        values: [
          ...currentProperty.values,
          {
            tagId: -1,
            name: newTag.name,
          },
        ],
      },
    ],
  };

  queryClient.setQueryData(
    getBidResponseByIdQueryKey,
    updatedCurrentBidResponse,
  );

  return previousGetBidResponseById;
}

export function useDeleteBidResponseTagMutation(bidResponseId: number) {
  const queryClient = useQueryClient();
  const getBidResponseByIdQueryKey = [getBidResponseById.name, bidResponseId];

  return useMutation({
    mutationFn: ({
      bidResponseId,
      propertyId,
      tagId,
    }: {
      bidResponseId: number;
      propertyId: number;
      tagId: number;
    }) => deleteBidResponseTag({ bidResponseId, propertyId, tagId }),
    onMutate: async ({ bidResponseId, propertyId, tagId }) => {
      await queryClient.cancelQueries({ queryKey: getBidResponsesQueryKey });
      await queryClient.cancelQueries({ queryKey: getBidResponseByIdQueryKey });

      const beOptimistic = (oldData: BidResponse) => {
        const property = oldData.properties.find(
          property => property.propertyId === propertyId,
        );

        if (!property) {
          return oldData;
        }

        const newValues = property.values.filter(
          value => value.tagId !== tagId,
        );

        return {
          ...oldData,
          properties: [
            ...oldData.properties.filter(
              property => property.propertyId !== propertyId,
            ),
            { ...property, values: newValues },
          ],
        };
      };

      const oldGetBidResponses =
        queryClient.getQueryData<GetBidResponsesResponseDTO>(
          getBidResponsesQueryKey,
        );

      queryClient.setQueryData(
        getBidResponsesQueryKey,
        (oldData: GetBidResponsesResponseDTO) => ({
          ...oldData,
          bidResponses: oldData.bidResponses.map(oldBidResponse =>
            oldBidResponse.id === bidResponseId
              ? beOptimistic(oldBidResponse)
              : oldBidResponse,
          ),
        }),
      );

      const oldGetBidResponseById = queryClient.getQueryData<BidResponse>(
        getBidResponseByIdQueryKey,
      );

      queryClient.setQueryData(getBidResponseByIdQueryKey, beOptimistic);

      return { oldGetBidResponses, oldGetBidResponseById };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        getBidResponsesQueryKey,
        context?.oldGetBidResponses,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponseByIdQueryKey });
      queryClient.invalidateQueries({ queryKey: getBidResponsesQueryKey });
    },
  });
}
